import {
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  Typography,
  useEventCallback,
} from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { ReactElement, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DocumentMarriageUploadFormVM } from 'civil-status/view-models/document-birth-form';
import MatricolaList from '../shared/matricola-list';
import ListGender from 'shell/components/shared/gender';
import { DatePicker } from '@mui/lab';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { HttpResponse } from 'shell/models/http-response';
import { ConvertedImage } from 'shell/models/convert-image';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { Cropper } from 'shell/components/shared/cropper';
import SelectSerie from '../shared/select-serie';
import SelectRegistry from '../shared/select-registry';
import ListCivilStatus from '../shared/list-civil-status';
import * as yup from 'yup';
import { commonSchemaObject } from 'civil-status/constants/common-schema';
import { marriageSchemaObject } from '../../../shell/constants/create-schema-marriage';

const validationSchema = yup.object().shape({
  ...marriageSchemaObject,
  ...commonSchemaObject,
});

interface FormDocumentMarriageUploadNewProps {
  onOk: (form: DocumentMarriageUploadFormVM) => void;
  onCancel: (form?: DocumentMarriageUploadFormVM) => void;
  title: string;
  children?: ReactElement;
  disabled?: boolean;
  isEdit?: boolean;
}

const FormDocumentMarriageUploadNew = ({
  onOk,
  onCancel,
  title,
  disabled = false,
}: FormDocumentMarriageUploadNewProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const form = useFormik<DocumentMarriageUploadFormVM>({
    initialValues: {
      year: '',
      registerNumber: '',
      documentNumber: '',
      portion: '',
      serie: '',
      serie2: '',
      marriageDate: null,
      marriageMunicipality: '',
      matricolaHusband: '',
      surnameHusband: '',
      nameHusband: '',
      genderHusband: '',
      birthdayHusband: null,
      citizenshipWife: '',
      citizenshipHusband: '',
      yearsHusband: '',
      yearDateHusband: '',
      municipalityBirthHusband: '',
      residenceMunicipalityHusband: '',
      statusHusband: '',
      surnameAfterMarriageHusband: '',
      matricolaWife: '',
      surnameWife: '',
      nameWife: '',
      genderWife: '',
      birthdayWife: null,
      yearsWife: '',
      yearDateWife: '',
      municipalityBirthWife: '',
      residenceMunicipalityWife: '',
      statusWife: '',
      surnameAfterMarriageWife: '',
    },
    onSubmit,
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
  });

  const convertDocument = useHttpClient<HttpResponse<ConvertedImage>>(
    `${__CIVIL_STATUS_API_URL__}/convert-image`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
    }
  );

  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};
      (
        convertDocument({
          data: {
            formato: 'png',
            immagineIniziale: (result as string)?.split(',')[1],
          },
        }) as ResponsePromise<HttpResponse<ConvertedImage>>
      ).then((response) => {
        form.setFieldValue('upload', {
          file,
          raw: response?.data?.payload?.immagineConvertita
            ? (response?.data?.payload?.immagineConvertita as Array<string>)?.map(
                (immagine: string) => `data:image/png;base64,${immagine}`
              )
            : [],
          rawTiff: result as string,
        });
      });
    });

    reader.readAsDataURL(file);
  });

  const handleCivilStatusListHusbandChange = useEventCallback((element: string | undefined) => {
    form.setFieldValue('statusHusband', element);
  });

  const handleCivilStatusListWifeChange = useEventCallback((element: string | undefined) => {
    form.setFieldValue('statusWife', element);
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={form.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Anno"
                    aria-label="Anno"
                    id="year"
                    fullWidth
                    inputProps={{
                      maxLength: '4',
                    }}
                    onChange={(event) => {
                      form.setFieldValue('year', event?.target.value);
                    }}
                    value={form.values.year || ''}
                    helperText={form.errors.year}
                    error={form.touched.year && Boolean(form.errors.year)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Atto"
                    aria-label="Atto"
                    fullWidth
                    id="document"
                    onChange={(event) => {
                      form.setFieldValue('documentNumber', event?.target.value);
                    }}
                    inputProps={{
                      maxLength: '4',
                    }}
                    value={form.values.documentNumber || ''}
                    helperText={form.errors.documentNumber}
                    error={form.touched.documentNumber && Boolean(form.errors.documentNumber)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <SelectRegistry
                    isSmallSize
                    label="Registro"
                    id="registry"
                    onChange={(event) => {
                      form.setFieldValue('registerNumber', event?.target.value);
                    }}
                    value={form.values.registerNumber || ''}
                    error={form.touched.registerNumber && Boolean(form.errors.registerNumber)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Parte"
                    aria-label="Parte"
                    fullWidth
                    id="portion"
                    onChange={(event) => {
                      form.setFieldValue('portion', event?.target.value);
                    }}
                    inputProps={{
                      maxLength: '2',
                    }}
                    value={form.values.portion || ''}
                    helperText={form.errors.portion}
                    error={form.touched.portion && Boolean(form.errors.portion)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <SelectSerie
                    isSmallSize
                    label="Serie"
                    id="serie"
                    onChange={(event) => {
                      form.setFieldValue('serie', event?.target.value);
                    }}
                    value={form.values.serie || ''}
                    error={form.touched.serie && Boolean(form.errors.serie)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }} md={4}>
                <DatePicker
                  label="Data matrimonio"
                  aria-label="Data matrimonio"
                  value={form.values?.marriageDate || null}
                  disabled={disabled}
                  onChange={(value) => form.setFieldValue('marriageDate', value)}
                  renderInput={(params) => (
                    <PmiTextField
                      id="marriageDate"
                      name="marriageDate"
                      {...params}
                      fullWidth
                      error={form.touched?.marriageDate && Boolean(form.errors?.marriageDate)}
                      helperText={form.errors?.marriageDate}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }} md={8}>
                <PmiTextField
                  aria-label="Comune matrimonio"
                  label="Comune matrimonio"
                  id="marriageMunicipality"
                  name="marriageMunicipality"
                  fullWidth
                  disabled={disabled}
                  value={form.values?.marriageMunicipality || ''}
                  onKeyDown={(event) => {
                    if (event.code === 'F2') {
                      event.preventDefault();
                      event.stopPropagation();
                      form.setFieldValue('marriageMunicipality', 'MILANO');
                      return;
                    }
                  }}
                  onChange={(event) => {
                    form.setFieldValue('marriageMunicipality', event?.target.value);
                  }}
                  error={
                    form.touched?.marriageMunicipality && Boolean(form.errors?.marriageMunicipality)
                  }
                  helperText={form.errors?.marriageMunicipality}
                />
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <Divider />
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <MatricolaList
                    aria-label="Matricola"
                    label="Matricola"
                    id="matricolaHusband"
                    disabled={disabled}
                    surname={form.values?.surnameHusband || ''}
                    name={form.values?.nameHusband || ''}
                    value={form.values?.matricolaHusband || ''}
                    dateBirth={form.values?.birthdayHusband || null}
                    onChanges={(value) =>
                      form.setValues({
                        ...form.values,
                        matricolaHusband: value.matricola,
                        birthdayHusband: value.birthday,
                        genderHusband: value.gender,
                        nameHusband: value.name,
                        surnameHusband: value.surname,
                      })
                    }
                    error={form.touched?.matricolaHusband && Boolean(form.errors?.matricolaHusband)}
                    isSmallSize
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Cognome"
                    label={`Cognome`}
                    id="surnameHusband"
                    name="surnameHusband"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.surnameHusband || ''}
                    onChange={(event) => form.setFieldValue('surnameHusband', event?.target.value)}
                    error={form.touched?.surnameHusband && Boolean(form.errors?.surnameHusband)}
                    helperText={form.errors?.surnameHusband}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Nome"
                    label={`Nome`}
                    id="nameHusband"
                    name="nameHusband"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.nameHusband || ''}
                    onChange={(event) => form.setFieldValue('nameHusband', event?.target.value)}
                    error={form.touched?.nameHusband && Boolean(form.errors?.nameHusband)}
                    helperText={form.errors?.nameHusband}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={3}>
                  <ListGender
                    value={form.values?.genderHusband || ''}
                    onChange={(event) => form.setFieldValue('genderHusband', event?.target.value)}
                    error={form.touched?.genderHusband && Boolean(form.errors?.genderHusband)}
                    disabled={disabled}
                    isSmallSize
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={4}>
                  <DatePicker
                    label="Data nascita"
                    aria-label="Data nascita"
                    value={form.values?.birthdayHusband || null}
                    disabled={disabled}
                    onChange={(value) => form.setFieldValue('birthdayHusband', value)}
                    renderInput={(params) => (
                      <PmiTextField
                        id="birthdayHusband"
                        name="birthdayHusband"
                        {...params}
                        fullWidth
                        error={
                          form.touched?.birthdayHusband && Boolean(form.errors?.birthdayHusband)
                        }
                        helperText={form.errors?.birthdayHusband}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={2}>
                  <PmiTextField
                    aria-label="Anni"
                    label="Anni"
                    id="year"
                    name="years"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.yearsHusband || ''}
                    onChange={(event) => form.setFieldValue('yearsHusband', event?.target.value)}
                    error={form.touched?.yearsHusband && Boolean(form.errors?.yearsHusband)}
                    helperText={form.errors?.yearsHusband}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={3}>
                  <PmiTextField
                    aria-label="Anno nascita"
                    label="Anno nascita"
                    id="year-birth"
                    name="year-birth"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.yearDateHusband || ''}
                    onChange={(event) => form.setFieldValue('yearDateHusband', event?.target.value)}
                    error={form.touched?.yearDateHusband && Boolean(form.errors?.yearDateHusband)}
                    helperText={form.errors?.yearDateHusband}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune nascita"
                    label="Comune nascita"
                    id="municipalityBirthHusband"
                    name="municipalityBirthHusband"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.municipalityBirthHusband || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('municipalityBirthHusband', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('municipalityBirthHusband', event?.target.value);
                    }}
                    error={
                      form.touched?.municipalityBirthHusband &&
                      Boolean(form.errors?.municipalityBirthHusband)
                    }
                    helperText={form.errors?.municipalityBirthHusband}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} md={9} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune residenza"
                    label="Comune residenza"
                    id="residenceMunicipalityHusband"
                    name="residenceMunicipalityHusband"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.residenceMunicipalityHusband || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('residenceMunicipalityHusband', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('residenceMunicipalityHusband', event?.target.value);
                    }}
                    error={
                      form.touched?.residenceMunicipalityHusband &&
                      Boolean(form.errors?.residenceMunicipalityHusband)
                    }
                    helperText={form.errors?.residenceMunicipalityHusband}
                  />
                </Grid>
                <Grid item xs={12} md={3} style={{ paddingTop: '10px' }}>
                  <span>F2 per Milano</span>
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={7}>
                  <PmiTextField
                    label="Cittadinanza"
                    aria-label="Cittadinanza"
                    id="citizenship-h"
                    name="citizenship-h"
                    fullWidth
                    value={form.values?.citizenshipHusband || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('citizenshipHusband', 'ITALIANA');
                        return;
                      }
                    }}
                    disabled={disabled}
                    onChange={(event) =>
                      form.setFieldValue('citizenshipHusband', event.target.value)
                    }
                    error={
                      form.touched?.citizenshipHusband && Boolean(form.errors?.citizenshipHusband)
                    }
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={5}>
                  <ListCivilStatus
                    id="civil-status-h"
                    value={form.values?.statusHusband || ''}
                    disabled={disabled}
                    onChange={(event) => handleCivilStatusListHusbandChange(event.target.value)}
                    error={form.touched?.statusHusband && Boolean(form.errors?.statusHusband)}
                  />
                </Grid>
                {/* <span>F2 per Italiana</span> */}
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Cognome post matrimonio"
                    label="Cognome post matrimonio"
                    id="surnameAfterMarriageHusband"
                    name="surnameAfterMarriageHusband"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.surnameAfterMarriageHusband || ''}
                    onChange={(event) =>
                      form.setFieldValue('surnameAfterMarriageHusband', event?.target.value)
                    }
                    error={
                      form.touched?.surnameAfterMarriageHusband &&
                      Boolean(form.errors?.surnameAfterMarriageHusband)
                    }
                    helperText={form.errors?.surnameAfterMarriageHusband}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <Divider />
              </Grid>
              {/* <Grid item xs={12} style={{ paddingTop: '10px' }}>
        <PmiTitleDivider>Moglie</PmiTitleDivider>
      </Grid> */}
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <MatricolaList
                    aria-label="Matricola"
                    label="Matricola"
                    id="matricolaWife"
                    surname={form.values?.surnameWife || ''}
                    name={form.values?.nameWife || ''}
                    dateBirth={form.values?.birthdayWife || null}
                    disabled={disabled}
                    value={form.values?.matricolaWife || ''}
                    onChanges={(value) =>
                      form.setValues({
                        ...form.values,
                        matricolaWife: value.matricola,
                        birthdayWife: value.birthday,
                        genderWife: value.gender,
                        nameWife: value.name,
                        surnameWife: value.surname,
                      })
                    }
                    error={form.touched?.matricolaWife && Boolean(form.errors?.matricolaWife)}
                    isSmallSize
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Cognome"
                    label={`Cognome`}
                    id="surnameWife"
                    name="surnameWife"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.surnameWife || ''}
                    onChange={(event) => form.setFieldValue('surnameWife', event?.target.value)}
                    error={form.touched?.surnameWife && Boolean(form.errors?.surnameWife)}
                    helperText={form.errors?.surnameWife}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Nome"
                    label={`Nome`}
                    id="nameWife"
                    name="nameWife"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.nameWife || ''}
                    onChange={(event) => form.setFieldValue('nameWife', event?.target.value)}
                    error={form.touched?.nameWife && Boolean(form.errors?.nameWife)}
                    helperText={form.errors?.nameWife}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={3}>
                  <ListGender
                    value={form.values?.genderWife || ''}
                    onChange={(event) => form.setFieldValue('genderWife', event?.target.value)}
                    error={form.touched?.genderWife && Boolean(form.errors?.genderWife)}
                    disabled={disabled}
                    isSmallSize
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={4}>
                  <DatePicker
                    label="Data nascita"
                    aria-label="Data nascita"
                    value={form.values?.birthdayWife || null}
                    disabled={disabled}
                    onChange={(value) => form.setFieldValue('birthdayWife', value)}
                    renderInput={(params) => (
                      <PmiTextField
                        id="birthdayWife"
                        name="birthdayWife"
                        {...params}
                        fullWidth
                        error={form.touched?.birthdayWife && Boolean(form.errors?.birthdayWife)}
                        helperText={form.errors?.birthdayWife}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={2}>
                  <PmiTextField
                    aria-label="Anni"
                    label="Anni"
                    id="year"
                    name="years"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.yearsWife || ''}
                    onChange={(event) => form.setFieldValue('yearsWife', event?.target.value)}
                    error={form.touched?.yearsWife && Boolean(form.errors?.yearsWife)}
                    helperText={form.errors?.yearsWife}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={3}>
                  <PmiTextField
                    aria-label="Anno nascita"
                    label="Anno nascita"
                    id="year-birth"
                    name="year-birth"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.yearDateWife || ''}
                    onChange={(event) => form.setFieldValue('yearDateWife', event?.target.value)}
                    error={form.touched?.yearDateWife && Boolean(form.errors?.yearDateWife)}
                    helperText={form.errors?.yearDateWife}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune nascita"
                    label="Comune nascita"
                    id="municipalityBirthWife"
                    name="municipalityBirthWife"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.municipalityBirthWife || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('municipalityBirthWife', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('municipalityBirthWife', event?.target.value);
                    }}
                    error={
                      form.touched?.municipalityBirthWife &&
                      Boolean(form.errors?.municipalityBirthWife)
                    }
                    helperText={form.errors?.municipalityBirthWife}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} md={9} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune residenza"
                    label="Comune residenza"
                    id="residenceMunicipalityWife"
                    name="residenceMunicipalityWife"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.residenceMunicipalityWife || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('residenceMunicipalityWife', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('residenceMunicipalityWife', event?.target.value);
                    }}
                    error={
                      form.touched?.residenceMunicipalityWife &&
                      Boolean(form.errors?.residenceMunicipalityWife)
                    }
                    helperText={form.errors?.residenceMunicipalityWife}
                  />
                </Grid>
                <Grid item xs={12} md={3} style={{ paddingTop: '10px' }}>
                  <span>F2 per Milano</span>
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={7}>
                  <PmiTextField
                    label="Cittadinanza"
                    aria-label="Cittadinanza"
                    id="citizenship-w"
                    name="citizenship-w"
                    fullWidth
                    value={form.values?.citizenshipWife || ''}
                    disabled={disabled}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('citizenshipWife', 'ITALIANA');
                        return;
                      }
                    }}
                    onChange={(event) => form.setFieldValue('citizenshipWife', event.target.value)}
                    error={form.touched?.citizenshipWife && Boolean(form.errors?.citizenshipWife)}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={5}>
                  <ListCivilStatus
                    id="civil-status-w"
                    value={form.values?.statusWife || ''}
                    disabled={disabled}
                    onChange={(event) => handleCivilStatusListWifeChange(event.target.value)}
                    error={form.touched?.statusWife && Boolean(form.errors?.statusWife)}
                  />
                </Grid>
                {/* <span>F2 per Italiana</span> */}
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Cognome post matrimonio"
                    label="Cognome post matrimonio"
                    id="surnameAfterMarriageWife"
                    name="surnameAfterMarriageWife"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.surnameAfterMarriageWife || ''}
                    onChange={(event) =>
                      form.setFieldValue('surnameAfterMarriageWife', event?.target.value)
                    }
                    error={
                      form.touched?.surnameAfterMarriageWife &&
                      Boolean(form.errors?.surnameAfterMarriageWife)
                    }
                    helperText={form.errors?.surnameAfterMarriageWife}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                Carica documento&nbsp;
                <PmiInputFile
                  accept="image/tiff"
                  multiple={false}
                  file={form.values.upload?.file}
                  showFilename
                  onChange={handleAddFile}
                  color="cdmBrandThird"
                  tipMaxSize={300}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} md={8}>
              <StickyDiv>
                <Cropper
                  images={form.values.upload?.raw || []}
                  disabled={!form.values?.upload?.raw?.length}
                />
              </StickyDiv>
            </Grid>
          </Grid>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(form.values);
            }}
          >
            Annulla
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormDocumentMarriageUploadNew.displayName = 'Form Document Birth new component';

export default FormDocumentMarriageUploadNew;
